import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchJson, postJson, putJson } from '../lib/api';

// --- Types ---

export interface ForecastPipelineBreakdown {
  pipelineId: string;
  pipelineName: string;
  dealCount: number;
  unweightedValue: number;
  weightedValue: number;
}

export interface ForecastOwnerBreakdown {
  userId: string;
  userName: string;
  dealCount: number;
  weightedValue: number;
  quota: number;
  quotaPercent: number;
}

export interface ForecastMonthly {
  month: string;
  weightedValue: number;
  unweightedValue: number;
  dealCount: number;
}

export interface Forecast {
  totalWeightedPipeline: number;
  totalUnweightedPipeline: number;
  totalDealCount: number;
  avgDealSize: number;
  byPipeline: ForecastPipelineBreakdown[];
  byOwner: ForecastOwnerBreakdown[];
  byMonth: ForecastMonthly[];
}

export interface Quota {
  id: string;
  userId: string;
  userName?: string;
  period: string;
  amount: number;
  createdAt: string;
  updatedAt: string;
}

export interface QuotaProgress {
  userId: string;
  userName?: string;
  period: string;
  quota: number;
  actual: number;
  percent: number;
}

// --- Hooks ---

export function useForecast(period?: string) {
  const params = period ? `?period=${period}` : '';
  return useQuery({
    queryKey: ['forecast', period],
    queryFn: () => fetchJson<Forecast>(`/api/forecasting${params}`),
  });
}

export function useQuotas(period?: string) {
  const params = period ? `?period=${period}` : '';
  return useQuery({
    queryKey: ['quotas', period],
    queryFn: () => fetchJson<{ quotas: Quota[] }>(`/api/forecasting/quotas${params}`),
  });
}

export function useSetQuota() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { userId: string; period: string; amount: number }) =>
      postJson<Quota>('/api/forecasting/quotas', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotas'] });
      queryClient.invalidateQueries({ queryKey: ['forecast'] });
    },
  });
}

export function useQuotaProgress(period?: string) {
  const params = period ? `?period=${period}` : '';
  return useQuery({
    queryKey: ['quota-progress', period],
    queryFn: () => fetchJson<{ progress: QuotaProgress[] }>(`/api/forecasting/quota-progress${params}`),
  });
}
